import React, { useState, useEffect } from 'react';
import { clampDate } from '../../utils/date';

const EntryForm = ({
  initialData = {},
  accounts = [],
  onSubmit,
  onClose,
  onReshoot
}) => {
  const [formData, setFormData] = useState({
    date: initialData.date || new Date().toISOString().split('T')[0],
    description: initialData.description || '',
    partner: initialData.partner || '',
    debit_account: initialData.debit_account || '',
    debit_amount: initialData.debit_amount || '',
    debit_tax_type: initialData.debit_tax_type || '課税仕入10%',
    credit_account: initialData.credit_account || '',
    credit_amount: initialData.credit_amount || '',
    credit_tax_type: initialData.credit_tax_type || '対象外'
  });

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatAmount = (value) => {
    const num = parseInt(value.replace(/[^\d]/g, ''));
    return isNaN(num) ? '' : new Intl.NumberFormat('ja-JP').format(num);
  };

  const parseAmount = (value) => {
    return parseInt(value.replace(/[^\d]/g, '')) || 0;
  };

  const debitAmount = parseAmount(formData.debit_amount);
  const creditAmount = parseAmount(formData.credit_amount);
  const isBalanced = debitAmount > 0 && debitAmount === creditAmount;

  const handleSubmit = () => {
    if (!formData.description) {
      alert('摘要を入力してください');
      return;
    }
    if (!isBalanced) {
      alert('借方と貸方の金額が一致していません');
      return;
    }
    if (!formData.debit_account || !formData.credit_account) {
      alert('科目を選択してください');
      return;
    }

    onSubmit({
      ...formData,
      amount: debitAmount
    });
  };

  const taxTypes = [
    '対象外',
    '課税売上10%',
    '課税仕入10%',
    '課税売上8%(軽)',
    '課税仕入8%(軽)',
    '非課税',
    '不課税'
  ];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      background: '#fff',
      paddingTop: '36px'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px 8px',
        borderBottom: '1px solid #ccc',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between'
      }}>
        <div>
          {onReshoot && (
            <button
              onClick={onReshoot}
              style={{
                fontSize: '12px',
                color: '#2B6CB0',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0
              }}
            >← 撮り直す</button>
          )}
          <h2 style={{
            fontSize: '16px',
            fontWeight: 700,
            color: '#333',
            margin: 0
          }}>仕訳入力</h2>
        </div>
        <button
          onClick={onClose}
          style={{
            fontSize: '12px',
            color: '#999',
            border: '1.5px solid #ccc',
            borderRadius: '50%',
            width: '26px',
            height: '26px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '2px',
            background: '#fff',
            cursor: 'pointer'
          }}
        >✕</button>
      </div>

      {/* Form scroll area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '8px 16px 80px',
        WebkitOverflowScrolling: 'touch'
      }}>
        {/* 基本情報 */}
        <div style={{
          fontSize: '9px',
          fontWeight: 600,
          color: '#999',
          marginTop: '12px',
          marginBottom: '4px',
          letterSpacing: '0.05em'
        }}>基本情報</div>

        <div style={{
          border: '1px solid #ddd',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px 12px',
            borderBottom: '1px solid #eee',
            gap: '8px'
          }}>
            <div style={{
              fontSize: '10px',
              color: '#999',
              width: '40px',
              flexShrink: 0,
              fontWeight: 500
            }}>日付</div>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleChange('date', clampDate(e.target.value))}
              style={{
                flex: 1,
                fontSize: '12px',
                color: '#333',
                border: 'none',
                background: 'transparent',
                outline: 'none'
              }}
            />
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px 12px',
            borderBottom: '1px solid #eee',
            gap: '8px'
          }}>
            <div style={{
              fontSize: '10px',
              color: '#999',
              width: '40px',
              flexShrink: 0,
              fontWeight: 500
            }}>摘要</div>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="（必須）取引の内容を入力"
              style={{
                flex: 1,
                fontSize: '12px',
                color: '#333',
                border: 'none',
                background: 'transparent',
                outline: 'none'
              }}
            />
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px 12px',
            gap: '8px'
          }}>
            <div style={{
              fontSize: '10px',
              color: '#999',
              width: '40px',
              flexShrink: 0,
              fontWeight: 500
            }}>取引先</div>
            <input
              type="text"
              value={formData.partner}
              onChange={(e) => handleChange('partner', e.target.value)}
              placeholder="（任意）"
              style={{
                flex: 1,
                fontSize: '12px',
                color: '#333',
                border: 'none',
                background: 'transparent',
                outline: 'none'
              }}
            />
          </div>
        </div>

        {/* 借方 */}
        <div style={{
          fontSize: '9px',
          fontWeight: 600,
          color: '#999',
          marginTop: '12px',
          marginBottom: '4px',
          letterSpacing: '0.05em'
        }}>借方</div>

        <div style={{
          border: '1px solid #ddd',
          borderLeft: '3px solid #E53E3E',
          borderRadius: '8px',
          marginBottom: '8px',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 10px',
            gap: '6px',
            borderBottom: '1px solid #eee'
          }}>
            <div style={{
              fontSize: '9px',
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: '4px',
              background: '#FFF5F5',
              color: '#E53E3E'
            }}>借方</div>
            <select
              value={formData.debit_account}
              onChange={(e) => handleChange('debit_account', e.target.value)}
              style={{
                flex: 1,
                fontSize: '12px',
                fontWeight: 500,
                border: 'none',
                background: 'transparent',
                outline: 'none',
                color: '#333'
              }}
            >
              <option value="">科目を選択</option>
              {accounts.map(acc => (
                <option key={acc.code} value={acc.name}>{acc.name}</option>
              ))}
            </select>
          </div>

          <div style={{
            padding: '8px 10px',
            display: 'flex',
            gap: '8px'
          }}>
            <div style={{ flex: 1.2 }}>
              <div style={{
                fontSize: '8px',
                color: '#999',
                fontWeight: 600,
                marginBottom: '2px'
              }}>金額</div>
              <input
                type="text"
                value={formData.debit_amount}
                onChange={(e) => handleChange('debit_amount', e.target.value)}
                placeholder="0"
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  background: '#fafafa',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '8px',
                color: '#999',
                fontWeight: 600,
                marginBottom: '2px'
              }}>税区分</div>
              <select
                value={formData.debit_tax_type}
                onChange={(e) => handleChange('debit_tax_type', e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '10px',
                  background: '#fafafa',
                  outline: 'none'
                }}
              >
                {taxTypes.map(tax => (
                  <option key={tax} value={tax}>{tax}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 貸方 */}
        <div style={{
          fontSize: '9px',
          fontWeight: 600,
          color: '#999',
          marginTop: '12px',
          marginBottom: '4px',
          letterSpacing: '0.05em'
        }}>貸方</div>

        <div style={{
          border: '1px solid #ddd',
          borderLeft: '3px solid #2B6CB0',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 10px',
            gap: '6px',
            borderBottom: '1px solid #eee'
          }}>
            <div style={{
              fontSize: '9px',
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: '4px',
              background: '#EBF8FF',
              color: '#2B6CB0'
            }}>貸方</div>
            <select
              value={formData.credit_account}
              onChange={(e) => handleChange('credit_account', e.target.value)}
              style={{
                flex: 1,
                fontSize: '12px',
                fontWeight: 500,
                border: 'none',
                background: 'transparent',
                outline: 'none',
                color: '#333'
              }}
            >
              <option value="">科目を選択</option>
              {accounts.map(acc => (
                <option key={acc.code} value={acc.name}>{acc.name}</option>
              ))}
            </select>
          </div>

          <div style={{
            padding: '8px 10px',
            display: 'flex',
            gap: '8px'
          }}>
            <div style={{ flex: 1.2 }}>
              <div style={{
                fontSize: '8px',
                color: '#999',
                fontWeight: 600,
                marginBottom: '2px'
              }}>金額</div>
              <input
                type="text"
                value={formData.credit_amount}
                onChange={(e) => handleChange('credit_amount', e.target.value)}
                placeholder="0"
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  background: '#fafafa',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '8px',
                color: '#999',
                fontWeight: 600,
                marginBottom: '2px'
              }}>税区分</div>
              <select
                value={formData.credit_tax_type}
                onChange={(e) => handleChange('credit_tax_type', e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '10px',
                  background: '#fafafa',
                  outline: 'none'
                }}
              >
                {taxTypes.map(tax => (
                  <option key={tax} value={tax}>{tax}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        borderTop: '1px solid #ccc',
        background: '#fff',
        display: 'flex',
        alignItems: 'center',
        padding: '10px 16px 20px',
        gap: '10px'
      }}>
        <div style={{
          flex: 1,
          fontSize: '9px',
          color: '#999',
          lineHeight: 1.6
        }}>
          借方 <span style={{
            fontFamily: 'monospace',
            fontWeight: 600,
            color: '#333',
            fontSize: '11px'
          }}>¥{formatAmount(String(debitAmount))}</span><br/>
          貸方 <span style={{
            fontFamily: 'monospace',
            fontWeight: 600,
            color: '#333',
            fontSize: '11px'
          }}>¥{formatAmount(String(creditAmount))}</span> {isBalanced ? '✓ 一致' : ''}
        </div>
        <button
          onClick={handleSubmit}
          disabled={!isBalanced || !formData.description}
          style={{
            padding: '10px 20px',
            border: '2px solid #333',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            background: isBalanced && formData.description ? '#333' : '#fff',
            color: isBalanced && formData.description ? '#fff' : '#999',
            whiteSpace: 'nowrap',
            cursor: isBalanced && formData.description ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s'
          }}
        >登録</button>
      </div>
    </div>
  );
};

export default EntryForm;
